// The per-project P&L view overlay: which of the organisation's categories one
// project renders, in what order, under what word.
//
// Run: deno test --allow-read src/features/pnlAnalytics/projectPnlView.test.ts
// Run: npm run verify:pnl-project-view
//
// WHERE THE OVERLAY LIVES. claims_project_config.config -> 'pnl_view', one row
// per project, validated by 20260915000100_pnl_project_view_config.sql. The
// keys are category NAME strings, not metric keys: every unaliased string
// resolves to expense.unmapped (20260908000200_pnl_category_alias.sql), so a
// key based overlay would hide unrelated lines together.
//
// THE ONE RULE NOT TO GET WRONG. The organisation list is the vocabulary and
// the compute set. resolveProjectPnlRows returns computeCategories unfiltered,
// always, and the P&L tab keeps handing that same full list to the engine.
// The tab builds its expense map by iterating the list it is given and then
// passes that list to calculateMonthlyPnl (ProjectStatusTab.tsx), so a category
// filtered out of it loses its money from totalDeductions and netProfit, while
// the company dashboard, which builds its map from the value rows themselves
// (useDashboardPnl.ts), goes on counting it. That divergence is the 8 September
// Aire defect, and filtering the compute set here would recreate it per
// project. Hiding changes rendering, never arithmetic.
//
// WHY A HIDDEN CATEGORY CAN STILL RENDER. Hiding is offered only for a category
// holding no value row on the project at all, so at the moment of hiding the
// row is worth zero everywhere. It does not stay worth zero by itself: the Xero
// sync upserts rows for any category (sync-xero-pnl/index.ts), a concurrent
// session can type a value, and an archive restore puts rows back. So a hidden
// category that holds a value renders anyway, with driftVisible set, and the
// money stays visible on the tab exactly as the dashboard counts it. That is
// the guard, not the mechanism.
//
// WHAT IS COSMETIC AND WHAT IS NOT. hidden and order change what the project's
// P&L renders and in what sequence. labels do not: the stored category string,
// the metric key and every figure are untouched, which is why a relabelled
// "Patching" still behaves as Patching for the two consumers that key off that
// literal string (claims_project_dashboard.pnl_patching and useDashboardPnl).
// Every write keys on row.category, never on row.label. A label reaching a
// value write would create a new category string and a fresh orphan.
//
// No React and no supabase here: this module only decides. The category type is
// declared structurally rather than imported from ../../hooks/usePnlCategories,
// mirroring src/lib/pnl/pnlCalculationCore.ts and ./renameDetection.ts: Deno
// resolves a type by loading its whole file, and usePnlCategories.ts
// transitively imports ToastModal.tsx (document) and supabase.ts
// (import.meta.env), neither available under Deno. It also means this module
// compiles against a category type that gains fields, such as SEQ-182's
// financial_inclusion, without being edited.

/** Structurally satisfied by src/hooks/usePnlCategories.ts's PnlCategory. */
export interface ProjectPnlCategoryLike {
  name: string;
  group: "Wages" | "Materials" | "Other";
}

/** One row of claims_pnl_monthly_values, as the P&L tab already holds it. */
export interface ProjectPnlValueRowLike {
  category: string;
  /** "YYYY-MM-01". */
  monthDate: string;
  amount: number;
  source: "manual" | "xero";
}

/**
 * The parsed overlay.
 *
 * Always fully populated after parseProjectPnlView, so no consumer has to think
 * about an absent key. Inherit everything is the empty overlay, not null.
 */
export interface ProjectPnlView {
  version: 1;
  hidden: string[];
  order: string[];
  labels: Record<string, string>;
}

export interface ProjectPnlRow {
  /** The organisation's canonical category name. Every write keys on this. */
  category: string;
  /** What the screen shows. Equal to category unless the project set a label. */
  label: string;
  group: "Wages" | "Materials" | "Other";
  /** True when label came from the overlay, so the canonical name can be shown beside it. */
  isRelabelled: boolean;
  /**
   * True when this row is in the overlay's hidden list and is rendered anyway,
   * because it holds a value. Drift, not normal operation. The caller renders a
   * chip and a one click unhide.
   */
  driftVisible: boolean;
}

export interface ResolvedProjectPnlView<C extends ProjectPnlCategoryLike> {
  /** What to render, in order. */
  rows: ProjectPnlRow[];
  /**
   * The organisation list, unfiltered and in its stored order. This is what the
   * engine must receive and what group subtotals must be computed over. See the
   * header.
   */
  computeCategories: C[];
  /** How many organisation categories the overlay actually removed from view. */
  hiddenCount: number;
  /**
   * Rendered categories holding no value row on this project, in any month,
   * from either source. These are the ones mode A may hide: hiding them cannot
   * change any figure, because they contribute zero to every figure already.
   */
  emptyCandidates: string[];
}

export const PNL_VIEW_VERSION = 1;

/** The overlay that changes nothing. */
export function inheritEverything(): ProjectPnlView {
  return { version: PNL_VIEW_VERSION, hidden: [], order: [], labels: {} };
}

const MAX_LABEL_LENGTH = 60;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Non empty strings only, de-duplicated, order preserved. */
function parseNameList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const names: string[] = [];
  for (const entry of value) {
    if (typeof entry !== "string") continue;
    const name = entry.trim();
    if (name === "" || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

function parseLabels(value: unknown): Record<string, string> {
  if (!isPlainObject(value)) return {};
  const labels: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw !== "string") continue;
    const name = key.trim();
    const label = raw.trim();
    if (name === "" || label === "" || label.length > MAX_LABEL_LENGTH) continue;
    labels[name] = label;
  }
  return labels;
}

/**
 * The overlay held in claims_project_config.config, made safe to use.
 *
 * Takes the whole config column, not the block, because that is what the row
 * carries and because an absent block is the normal case rather than an error.
 *
 * Never throws, for anything. This reads a jsonb column that the database
 * validates on write but that older rows, a hand fix or a future key can still
 * put something unexpected into, and a P&L tab that throws because one project
 * holds a malformed overlay is a far worse outcome than a tab that ignores it.
 * Anything unrecognised degrades to inherit, one key at a time: a broken
 * hidden list does not discard a good label map.
 */
export function parseProjectPnlView(config: unknown): ProjectPnlView {
  if (!isPlainObject(config)) return inheritEverything();

  const block = config.pnl_view;
  if (!isPlainObject(block)) return inheritEverything();
  if (block.version !== PNL_VIEW_VERSION) return inheritEverything();

  return {
    version: PNL_VIEW_VERSION,
    hidden: parseNameList(block.hidden),
    order: parseNameList(block.order),
    labels: parseLabels(block.labels),
  };
}

/** The overlay as it should be stored back into config.pnl_view. */
export function serialiseProjectPnlView(view: ProjectPnlView): Record<string, unknown> {
  return {
    version: PNL_VIEW_VERSION,
    hidden: [...view.hidden],
    order: [...view.order],
    labels: { ...view.labels },
  };
}

/** Every category name holding at least one value row, any month, either source. */
export function valuedCategories(rows: Iterable<ProjectPnlValueRowLike>): Set<string> {
  const valued = new Set<string>();
  for (const row of rows) {
    // Deliberately no test on amount and none on source. A zero someone typed
    // is still their data, and a category with only Xero rows would lose its
    // comparison sub-row if it were hidden. Deliberately no test on monthDate
    // either: the question is project wide, across every month, never the month
    // range the user happens to have selected. A range limited test would let
    // someone hide a category in one range that holds values in another, and
    // the row would then be missing from a range they can select one click
    // later with its money still inside the tab's totals.
    if (typeof row.category === "string" && row.category !== "") valued.add(row.category);
  }
  return valued;
}

/**
 * The organisation categories holding no value row on this project.
 *
 * Project wide and both sources, for the reason in valuedCategories.
 */
export function emptyCandidatesFor(
  orgCategories: readonly ProjectPnlCategoryLike[],
  rows: Iterable<ProjectPnlValueRowLike>,
): string[] {
  const valued = valuedCategories(rows);
  return orgCategories.map((c) => c.name).filter((name) => !valued.has(name));
}

export interface ResolveProjectPnlRowsInput<C extends ProjectPnlCategoryLike> {
  /** The organisation list as loaded, in its stored order. */
  orgCategories: C[];
  /** The parsed overlay, or null for a project that has never set one. */
  view: ProjectPnlView | null;
  /** Category names holding at least one value row on this project. */
  valued: Iterable<string>;
}

export function resolveProjectPnlRows<C extends ProjectPnlCategoryLike>(
  input: ResolveProjectPnlRowsInput<C>,
): ResolvedProjectPnlView<C> {
  const { orgCategories } = input;
  const view = input.view ?? inheritEverything();
  const valued = input.valued instanceof Set ? input.valued : new Set(input.valued);
  const hidden = new Set(view.hidden);

  const byName = new Map<string, C>();
  for (const category of orgCategories) {
    if (!byName.has(category.name)) byName.set(category.name, category);
  }

  // Rule 3: names the project ordered first, in that order, then everything
  // else in organisation order. An order entry naming a category the
  // organisation no longer has is inert, never an error and never pruned, so a
  // restored category comes back in its place.
  const ordered: C[] = [];
  const placed = new Set<string>();
  for (const name of view.order) {
    const category = byName.get(name);
    if (category && !placed.has(name)) {
      ordered.push(category);
      placed.add(name);
    }
  }
  for (const category of orgCategories) {
    if (!placed.has(category.name)) {
      ordered.push(category);
      placed.add(category.name);
    }
  }

  const rows: ProjectPnlRow[] = [];
  for (const category of ordered) {
    const isHidden = hidden.has(category.name);
    const holdsValue = valued.has(category.name);
    // Rule 2, and the drift guard: hidden removes the row only while the
    // category is worth nothing. The moment it holds a value it renders again,
    // because the alternative is money on the dashboard with no row on the tab.
    if (isHidden && !holdsValue) continue;

    const label = view.labels[category.name];
    const isRelabelled = typeof label === "string" && label.trim() !== "";
    rows.push({
      category: category.name,
      label: isRelabelled ? label.trim() : category.name,
      group: category.group,
      isRelabelled,
      driftVisible: isHidden,
    });
  }

  const renderedNames = new Set(rows.map((r) => r.category));

  return {
    // Rule 1. Unfiltered, always. See the header.
    computeCategories: [...orgCategories],
    rows,
    hiddenCount: orgCategories.length - rows.length,
    emptyCandidates: orgCategories
      .map((c) => c.name)
      .filter((name) => renderedNames.has(name) && !valued.has(name)),
  };
}

/** The overlay with one category hidden, or shown again when hide is false. */
export function withHidden(view: ProjectPnlView, category: string, hide = true): ProjectPnlView {
  const name = category.trim();
  if (name === "") return view;
  const without = view.hidden.filter((n) => n !== name);
  return { ...view, hidden: hide ? [...without, name] : without };
}

/**
 * The overlay with an explicit order.
 *
 * Stored as given, minus blanks and duplicates. Names the organisation does not
 * have are kept rather than dropped, for the same reason the resolver ignores
 * them rather than pruning them: a category removed at organisation level and
 * restored later should come back where the project put it.
 */
export function withOrder(view: ProjectPnlView, order: readonly string[]): ProjectPnlView {
  return { ...view, order: parseNameList([...order]) };
}

/** The overlay with one label set, or cleared when the text is blank. */
export function withLabel(view: ProjectPnlView, category: string, label: string): ProjectPnlView {
  const name = category.trim();
  if (name === "") return view;
  const text = label.trim();
  const labels = { ...view.labels };
  if (text === "" || text === name || text.length > MAX_LABEL_LENGTH) {
    delete labels[name];
  } else {
    labels[name] = text;
  }
  return { ...view, labels };
}

/**
 * The overlay after the organisation renamed a category.
 *
 * The overlay keys on the category NAME, so without this a rename would leave
 * every project's entry inert: rule 5 ignores a name the organisation no longer
 * has, and the line would reappear on every project unhidden, unordered and
 * under the organisation's new word. Survivable, and wrong.
 *
 * All three keys move together, because all three are the same project's
 * decision about the same line. Position is preserved in both arrays, so a
 * rename never silently reorders a project's P&L.
 *
 * Carried into the database by carryRenamedCategoryOverlays, in the same
 * editor save that already carries the value rows.
 */
export function withRenamedCategory(
  view: ProjectPnlView,
  oldName: string,
  newName: string,
): ProjectPnlView {
  const from = oldName.trim();
  const to = newName.trim();
  if (from === "" || to === "" || from === to) return view;

  const move = (names: string[]): string[] => {
    if (!names.includes(from)) return names;
    // The renamed entry keeps its position, and a pre-existing entry for the
    // new name is absorbed rather than duplicated.
    return names
      .map((name) => (name === from ? to : name))
      .filter((name, index, all) => all.indexOf(name) === index);
  };

  const labels: Record<string, string> = {};
  for (const [name, label] of Object.entries(view.labels)) {
    if (name === to && Object.hasOwn(view.labels, from)) continue;
    labels[name === from ? to : name] = label;
  }

  return { ...view, hidden: move(view.hidden), order: move(view.order), labels };
}

export interface ClearReachMonth {
  /** "YYYY-MM-01". */
  monthDate: string;
  manual: number;
  xero: number;
  total: number;
}

export interface ClearReach {
  /** The canonical category name, never the project's label for it. */
  category: string;
  /** Every month holding a value, in month order, with its amounts. */
  months: ClearReachMonth[];
  manualTotal: number;
  xeroTotal: number;
  /** manualTotal + xeroTotal, and therefore the sum of every row listed. */
  total: number;
  manualRowCount: number;
  xeroRowCount: number;
  /** True when clearing would remove Xero rows, which the next sync brings back. */
  includesXero: boolean;
}

/**
 * What a mode B clear would actually remove, from the rows it is given.
 *
 * This is a money statement made before a destructive act, so it is derived
 * from the same rows the caller then sends to the RPC, and the RPC re-reads
 * them and refuses if the set has changed. A dialog that states a reach it
 * cannot guarantee is the failure this area keeps producing.
 */
export function describeClearReach(
  category: string,
  rows: readonly ProjectPnlValueRowLike[],
): ClearReach {
  const byMonth = new Map<string, ClearReachMonth>();
  let manualTotal = 0;
  let xeroTotal = 0;
  let manualRowCount = 0;
  let xeroRowCount = 0;

  for (const row of rows) {
    let month = byMonth.get(row.monthDate);
    if (!month) {
      month = { monthDate: row.monthDate, manual: 0, xero: 0, total: 0 };
      byMonth.set(row.monthDate, month);
    }
    if (row.source === "xero") {
      month.xero += row.amount;
      xeroTotal += row.amount;
      xeroRowCount += 1;
    } else {
      month.manual += row.amount;
      manualTotal += row.amount;
      manualRowCount += 1;
    }
    month.total = month.manual + month.xero;
  }

  return {
    category,
    months: [...byMonth.values()].sort((a, b) => a.monthDate.localeCompare(b.monthDate)),
    manualTotal,
    xeroTotal,
    total: manualTotal + xeroTotal,
    manualRowCount,
    xeroRowCount,
    includesXero: xeroRowCount > 0,
  };
}
